// Remembering what the household buys — without remembering oopsies.
//
// The rules (the server applies them; devices use the same code to show suggestions offline):
//  • An item is remembered when it's bought (checked off). Something added and then deleted without
//    being bought — a typo, a mistake, "never mind" — leaves no trace.
//  • A bought item whose name looks like a typo of something already known ("bananna" when
//    "banana" is known) is not remembered either.
//  • Moving an item to another section remembers the section for that name, bought or not.
//  • Anyone can forget a memory (it comes back only if it's bought again).
//  • Things bought once more than a year ago stop being suggested.

import { allowedTypos, dictionary, editDistance } from './categorize';
import { itemKey, singularize, words } from './normalize';
import type { Memory } from './types';

const YEAR_MS = 365 * 86_400_000;

/** Offered as a suggestion while typing. */
export function isSuggestable(m: Memory, now = Date.now()): boolean {
  if (m.timesBought < 1) return false;
  if (m.timesBought === 1 && m.lastBoughtAt && now - Date.parse(m.lastBoughtAt) > YEAR_MS) return false;
  return true;
}

let dictWords: Set<string> | null = null;
/** Every word the dictionary knows (singular). */
function dictionaryWords(): Set<string> {
  if (!dictWords) {
    dictWords = new Set();
    for (const k of dictionary().keys()) for (const w of k.split(' ')) if (w.length > 2) dictWords.add(w);
  }
  return dictWords;
}

/**
 * "Did you mean …?": the name with misspelled words replaced by known ones, or null when it looks fine.
 * Known = the dictionary plus what the household has bought. Short words and words with digits are left alone.
 */
export function suggestCorrection(input: string, memories: Iterable<Memory> = []): string | null {
  const key = itemKey(input);
  if (!key) return null;
  const known = new Set(dictionaryWords());
  const knownKeys = new Set<string>();
  for (const m of memories) {
    if (!isSuggestable(m)) continue;
    knownKeys.add(m.key);
    for (const w of m.key.split(' ')) known.add(w);
  }
  if (knownKeys.has(key) || dictionary().has(key)) return null;
  const original = words(input.replace(/^\s*\d[^a-z]*/i, ''));
  let changed = false;
  const fixed = original.map((w) => {
    const s = singularize(w);
    if (known.has(s) || w.length < 4 || /\d/.test(w)) return w;
    const max = allowedTypos(s);
    let best: { word: string; d: number } | null = null;
    for (const k of known) {
      if (Math.abs(k.length - s.length) > max) continue;
      const d = editDistance(s, k, max);
      if (d <= max && (!best || d < best.d || (d === best.d && k < best.word))) best = { word: k, d };
    }
    if (!best) return w;
    changed = true;
    // keep a plural if they typed one
    return s !== w && w.endsWith('s') ? `${best.word}s` : best.word;
  });
  return changed ? fixed.join(' ') : null;
}

/** True when bought-ing this name shouldn't create a memory: it's a near-miss of something known. */
export function looksLikeTypo(name: string, memories: Iterable<Memory> = []): boolean {
  return suggestCorrection(name, memories) !== null;
}

export interface Suggestion {
  memory: Memory;
  /** Higher is better. */
  score: number;
}

/**
 * Remembered items matching what's typed so far: word starts first ("ba" → bananas, bagels,
 * "baby spinach"), then anywhere in the name, then near-misses. Frequent and recent ones rank higher.
 */
export function suggest(query: string, memories: Iterable<Memory>, opts: { limit?: number; exclude?: Set<string>; now?: number } = {}): Suggestion[] {
  const q = words(query).join(' ');
  const qKey = itemKey(query);
  if (!q) return [];
  const now = opts.now ?? Date.now();
  const out: Suggestion[] = [];
  for (const m of memories) {
    if (!isSuggestable(m, now) || opts.exclude?.has(m.key)) continue;
    const name = words(m.name).join(' ');
    let base = 0;
    if (name === q || m.key === qKey) base = 100;
    else if (name.startsWith(q) || m.key.startsWith(qKey)) base = 80;
    else if (name.split(' ').some((w) => w.startsWith(q)) || (q.includes(' ') && name.includes(q))) base = 60;
    else if (q.length >= 3 && name.includes(q)) base = 40;
    else if (q.length >= 4) {
      const max = allowedTypos(q);
      const prefix = name.slice(0, q.length);
      if (editDistance(q, prefix, max) <= max || name.split(' ').some((w) => editDistance(q, w.slice(0, q.length), max) <= max)) base = 20;
    }
    if (!base) continue;
    const ageDays = m.lastBoughtAt ? (now - Date.parse(m.lastBoughtAt)) / 86_400_000 : 365;
    const score = base + Math.min(15, Math.log2(1 + m.timesBought) * 4) + Math.max(0, 5 - ageDays / 30);
    out.push({ memory: m, score });
  }
  return out.sort((a, b) => b.score - a.score || a.memory.name.localeCompare(b.memory.name)).slice(0, opts.limit ?? 6);
}

/** key → category for categorize(), from the household's memories. */
export function learnedCategories(memories: Iterable<Memory>): Map<string, string> {
  const m = new Map<string, string>();
  for (const x of memories) if (x.categoryId) m.set(x.key, x.categoryId);
  return m;
}
